// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect } from "react";
import { useRegion } from "../context/RegionContext";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import OffersDialog from "./OffersDialog";
import { 
    getCartFromStorage, 
    saveCartToStorage, 
    getCouponsFromStorage, 
    saveCouponsToStorage, 
    clearCart 
} from "./cartUtils";
import "./Cart.css";
import LogoLoader from "../components/LogoLoader";

// Helper function to ensure image URL is properly formatted
const getImageUrl = (imageUrl) => {
    if (!imageUrl || imageUrl.trim() === '') return '';
    
    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
    }
    
    // If it's a relative path, add the base URL
    if (imageUrl.startsWith('/')) {
        return `${window.location.origin}${imageUrl}`;
    }
    
    // If it doesn't start with /, assume it's relative to the public folder
    return `${window.location.origin}/${imageUrl}`;
};

// Cart update functions

const updateCartItemQuantity = (itemId, newQuantity) => {
    const cart = getCartFromStorage();
    const itemIndex = cart.findIndex(item => item.id === itemId);
    
    if (itemIndex >= 0) {
        if (newQuantity <= 0) {
            cart.splice(itemIndex, 1);
        } else {
            cart[itemIndex].quantity = Math.min(10, newQuantity);
        }
        saveCartToStorage(cart);
    }
    
    return cart;
};

const removeFromCart = (itemId) => {
    const cart = getCartFromStorage();
    const updatedCart = cart.filter(item => item.id !== itemId);
    saveCartToStorage(updatedCart);
    return updatedCart;
};



export default function Cart() {
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupons, setAppliedCoupons] = useState([]);
    const [couponError, setCouponError] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [isOffersDialogOpen, setIsOffersDialogOpen] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const { selectedCountry } = useRegion();

    // Logo Loader State
    const [showLoader, setShowLoader] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);

    const getMinOrderValue = (coupon) => {
        if (selectedCountry && coupon.minimumOrderValueByCountry && Array.isArray(coupon.minimumOrderValueByCountry)) {
            const countryMin = coupon.minimumOrderValueByCountry.find(c => c.countryCode === selectedCountry);
            if (countryMin) return countryMin.amount;
        }
        return coupon.minimumOrderValue || 0;
    };

    useEffect(() => {
        const loadCart = async () => {
            const cart = getCartFromStorage();
            setCartItems(cart);
            
            // Load coupons
            const coupons = getCouponsFromStorage();
            setAppliedCoupons(coupons);
            
            setLoading(false);

            // Sync prices with backend
            if (cart.length > 0) {
                try {
                    const syncPromises = cart.map(async (item) => {
                        const productId = item.productId || (item.id && item.id.split('_')[0]);
                        if (!productId) return item;

                        const countryParam = `country=${selectedCountry || 'IN'}`;
                        let updatedProduct = null;

                        // 1. Try fetching by ID
                        try {
                            const response = await fetch(`${API_ENDPOINTS.STORE.PRODUCTS}/${productId}?${countryParam}`);
                            const data = await response.json();
                            if (data.success && data.data) {
                                updatedProduct = data.data;
                            }
                        } catch (e) {
                            console.warn(`ID sync failed for ${productId}`, e);
                        }

                        // 2. Fallback to Slug if ID failed (Product might have been recreated)
                        if (!updatedProduct && item.slug) {
                            try {
                                const slugResponse = await fetch(`${API_ENDPOINTS.STORE.PRODUCTS}?slug=${item.slug}&${countryParam}`);
                                const slugData = await slugResponse.json();
                                if (slugData.success && slugData.data && slugData.data.length > 0) {
                                    updatedProduct = slugData.data[0];
                                }
                            } catch (e) {
                                console.warn(`Slug sync failed for ${item.slug}`, e);
                            }
                        }

                        if (updatedProduct) {
                            return {
                                ...item,
                                productId: updatedProduct._id, // Update to current ID if changed
                                title: updatedProduct.title,
                                price: updatedProduct.offerPrice || updatedProduct.price,
                                currency: updatedProduct.currency,
                                currencySymbol: updatedProduct.currencySymbol,
                                slug: updatedProduct.slug,
                                // Maintain quantity and variants from cart
                            };
                        }
                        return item; // Fallback to existing data if both fail
                    });

                    const updatedCart = await Promise.all(syncPromises);
                    setCartItems(updatedCart);
                    saveCartToStorage(updatedCart);
                } catch (error) {
                    console.error('Failed to sync cart prices:', error);
                }
            }
        };

        loadCart();
        
        // Initial loading effect
        let progress = 0;
        const interval = setInterval(() => {
            const isDataLoaded = !loading;
            const maxProgress = isDataLoaded ? 100 : 90;
            
            if (progress < maxProgress) {
                const increment = Math.random() * 15;
                progress = Math.min(progress + increment, maxProgress);
                setLoadingProgress(progress);
            }

            if (isDataLoaded && progress >= 100) {
                clearInterval(interval);
                setTimeout(() => setShowLoader(false), 500);
            }
        }, 100);

        // Listen for storage changes
        const handleStorageChange = (e) => {
            if (e.key === 'cart') {
                setCartItems(getCartFromStorage());
            }
            if (e.key === 'appliedCoupons') {
                setAppliedCoupons(getCouponsFromStorage());
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, [selectedCountry, loading]);

    // Save coupons whenever they change
    useEffect(() => {
        if (!loading) {
            saveCouponsToStorage(appliedCoupons);
        }
    }, [appliedCoupons, loading]);

    const handleQuantityChange = (itemId, newQuantity) => {
        const updatedCart = updateCartItemQuantity(itemId, newQuantity);
        setCartItems(updatedCart);
    };

    const handleRemoveItem = (itemId) => {
        const updatedCart = removeFromCart(itemId);
        setCartItems(updatedCart);
    };

    const handleClearCart = () => {
        const updatedCart = clearCart();
        setCartItems(updatedCart);
        setAppliedCoupons([]);
    };

    const calculateSubtotal = () => {
        return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    const calculateDiscount = () => {
        let totalDiscount = 0;
        const subtotal = calculateSubtotal();
        
        appliedCoupons.forEach(coupon => {
            // Region check - ensure coupon is applicable to current country
            if (coupon.discountByCountry && Array.isArray(coupon.discountByCountry) && coupon.discountByCountry.length > 0) {
                const isApplicable = coupon.discountByCountry.some(c => c.countryCode === selectedCountry);
                if (!isApplicable) return;
            }

            // value check - if subtotal is less than minimum order value, skip this coupon
            const minOrder = getMinOrderValue(coupon);
            if (minOrder && subtotal < minOrder) {
                return;
            }

            // Determine discount type: support both new (discountType) and old (couponType) schemas
            const isPercent = coupon.discountType 
                ? coupon.discountType === 'PERCENT' 
                : coupon.couponType === true;
            
            // Get discount value based on type
            const getDiscountValue = () => {
                if (isPercent) {
                    // For PERCENT: use discountValue (new) or offerValue (old)
                    return coupon.discountValue || coupon.offerValue || 0;
                } else {
                    // For FIXED: use discountByCountry (new) or offerValue (old)
                    if (coupon.discountByCountry && Array.isArray(coupon.discountByCountry) && coupon.discountByCountry.length > 0) {
                        // Pick the country entry matching user's selection
                        const countryDiscount = coupon.discountByCountry.find(c => c.countryCode === selectedCountry);
                        if (countryDiscount) return countryDiscount.amount;
                        
                        // Fallback to first if match not found (or default)
                        return coupon.discountByCountry[0].amount || 0;
                    }
                    return coupon.offerValue || 0;
                }
            };
            
            const discountVal = getDiscountValue();

            if (coupon.appliesToAll) {
                // Apply to entire cart
                if (isPercent) {
                    totalDiscount += (subtotal * discountVal) / 100;
                } else {
                    totalDiscount += discountVal;
                }
            } else {
                // Apply to specific products
                coupon.applicableProducts.forEach(product => {
                    const productId = product && typeof product === 'object' && product._id 
                        ? String(product._id) 
                        : String(product);
                    
                    const cartItem = cartItems.find(item => {
                        const itemProductId = String(item.productId || item._id || item.id);
                        return itemProductId === productId;
                    });
                    
                    if (cartItem) {
                        const itemTotal = cartItem.price * cartItem.quantity;
                        if (isPercent) {
                            totalDiscount += (itemTotal * discountVal) / 100;
                        } else {
                            totalDiscount += discountVal * cartItem.quantity;
                        }
                    }
                });
            }
        });
        
        return Math.min(totalDiscount, subtotal); // Don't allow negative total
    };

    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        const discount = calculateDiscount();
        return subtotal - discount;
    };

    const handleApplyCoupon = async (couponCodeToApply = null) => {
        // Ensure we have a string, not an object
        let codeToUse;
        
        if (couponCodeToApply !== null && couponCodeToApply !== undefined) {
            // If it's already a string, use it
            if (typeof couponCodeToApply === 'string') {
                codeToUse = couponCodeToApply;
            }
            // If it's an object with a 'code' property, extract it
            else if (typeof couponCodeToApply === 'object' && couponCodeToApply !== null && couponCodeToApply.code) {
                codeToUse = String(couponCodeToApply.code);
            }
            // Otherwise, try to convert to string (but log warning)
            else {
                console.warn('Unexpected couponCodeToApply type:', typeof couponCodeToApply, couponCodeToApply);
                codeToUse = String(couponCodeToApply);
            }
        } else {
            // Use the couponCode state
            if (typeof couponCode === 'string') {
                codeToUse = couponCode.trim();
            } else if (couponCode && typeof couponCode === 'object' && couponCode.code) {
                codeToUse = String(couponCode.code);
            } else {
                codeToUse = String(couponCode || '').trim();
            }
        }
        
        // Final validation - ensure it's a valid string
        if (!codeToUse || codeToUse === 'undefined' || codeToUse === 'null' || codeToUse.toLowerCase() === '[object object]') {
            setCouponError('Please enter a valid coupon code');
            console.error('Invalid coupon code:', codeToUse, 'couponCodeToApply:', couponCodeToApply, 'couponCode state:', couponCode);
            return;
        }

        // Normalize to uppercase string
        codeToUse = codeToUse.toUpperCase().trim();

        // Check if coupon already applied
        if (appliedCoupons.some(c => c.code === codeToUse)) {
            setCouponError('This coupon code is already applied');
            return;
        }

        setCouponLoading(true);
        setCouponError('');

        try {
            const response = await fetch(`${API_ENDPOINTS.STORE.COUPON(codeToUse)}${codeToUse.includes('?') ? '&' : '?'}country=${selectedCountry || 'IN'}`);
            
            // Check if response is ok before trying to parse JSON
            if (!response.ok) {
                let errorMessage = 'Invalid coupon code';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (jsonError) {
                    // If JSON parsing fails, use status text or default message
                    errorMessage = response.statusText || 'Invalid coupon code';
                    if (response.status === 404) {
                        errorMessage = 'Coupon code not found';
                    } else if (response.status === 500) {
                        errorMessage = 'Server error. Please try again.';
                    }
                }
                setCouponError(errorMessage);
                setCouponLoading(false);
                return;
            }

            const data = await response.json();

            if (!data.success) {
                setCouponError(data.message || 'Invalid coupon code');
                setCouponLoading(false);
                return;
            }

            const coupon = data.data;
            
            // Additional validation on frontend (backend already checks, but double-check for safety)
            if (!coupon) {
                setCouponError('Coupon code not found');
                setCouponLoading(false);
                return;
            }
            
            // Check visibility
            if (coupon.offerIsVisible === false) {
                setCouponError('This coupon code is not available');
                setCouponLoading(false);
                return;
            }

            // Check if coupon is applicable to the selected country
            if (coupon.discountByCountry && Array.isArray(coupon.discountByCountry) && coupon.discountByCountry.length > 0) {
                const isApplicable = coupon.discountByCountry.some(c => c.countryCode === selectedCountry);
                if (!isApplicable) {
                    setCouponError(`This coupon is not applicable in your region (${selectedCountry})`);
                    setCouponLoading(false);
                    return;
                }
            }

            // Check minimum order value
            const minOrder = getMinOrderValue(coupon);
            if (minOrder && minOrder > 0) {
                const subtotal = calculateSubtotal();
                if (subtotal < minOrder) {
                    const symbol = (cartItems[0] && cartItems[0].currencySymbol) || '₹';
                    setCouponError(`This coupon requires a minimum order value of ${symbol}${minOrder.toLocaleString()}`);
                    setCouponLoading(false);
                    return;
                }
            }
            
            // Check date range
            const now = new Date();
            const startDate = new Date(coupon.offerStartDate);
            const endDate = new Date(coupon.offerEndDate);
            
            // Set time to start/end of day for accurate comparison
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(23, 59, 59, 999);
            
            if (now < startDate) {
                setCouponError('This coupon code is not yet available');
                setCouponLoading(false);
                return;
            }
            
            if (now > endDate) {
                setCouponError('This coupon code has expired');
                setCouponLoading(false);
                return;
            }
            
            // Check if coupon applies to cart items
            // Use consistent wording: "Applies to All Products" when product array is empty
            // Cart items use 'productId' field, not '_id' or 'id'
            const cartProductIds = cartItems.map(item => String(item.productId || item._id || item.id)).filter(Boolean);
            let applicableProducts = [];
            let appliesToAll = false;

            // Check if coupon applies to all products (empty product array means "Applies to All Products")
            if (!coupon.product || coupon.product.length === 0) {
                // Empty product array means "Applies to All Products"
                appliesToAll = true;
            } else {
                // Check which products in the coupon match cart items
                // Handle both populated products (with _id) and ObjectId strings
                const couponProductIds = coupon.product.map(p => {
                    // If p is a populated object, use _id
                    if (p && typeof p === 'object' && p._id) {
                        return String(p._id);
                    }
                    // If p is already a string/ObjectId, use it directly
                    return String(p);
                });
                
                // Check if any coupon product IDs match cart product IDs
                const hasMatchingProduct = couponProductIds.some(couponId => 
                    cartProductIds.some(cartId => String(couponId) === String(cartId))
                );

                if (!hasMatchingProduct) {
                    setCouponError('This coupon does not apply to any products in your cart');
                    setCouponLoading(false);
                    return;
                }
                
                // Store the matching product IDs for discount calculation
                applicableProducts = coupon.product.filter(p => {
                    const couponId = p && typeof p === 'object' && p._id ? String(p._id) : String(p);
                    return cartProductIds.some(cartId => String(couponId) === String(cartId));
                });
            }

            // Add coupon to applied list
            // Store the full coupon data including populated product details
            setAppliedCoupons(prev => [...prev, {
                ...coupon,
                code: coupon.code,
                appliesToAll,
                applicableProducts: appliesToAll ? [] : applicableProducts,
                // Store the full product array with populated data for display
                productData: coupon.product || []
            }]);

            setCouponCode('');
            setCouponError('');
        } catch (error) {
            console.error('Error applying coupon:', error);
            setCouponError('Failed to apply coupon. Please try again.');
        } finally {
            setCouponLoading(false);
        }
    };

    const handleRemoveCoupon = (couponCode) => {
        setAppliedCoupons(prev => prev.filter(c => c.code !== couponCode));
    };

    if (showLoader) {
        return <LogoLoader progress={loadingProgress} />;
    }

    if (cartItems.length === 0) {
        return (
            <div className="cart-empty mx-auto my-[2%] w-full max-w-[1200px] px-[20px]">
                <div className="cart-empty-content flex min-h-[280px] flex-col items-center justify-center rounded-[15px] bg-bg p-[24px] text-center shadow-[0_4px_20px_rgba(0,0,0,0.1)]">
                    <h2>Your cart is empty</h2>
                    <p>Looks like you haven't added any products to your cart yet.</p>
                    <Link to="/store" className="continue-shopping-btn mt-[10px] inline-flex rounded-[8px] bg-[#47b5ff] px-[16px] py-[10px] font-[600] text-white no-underline transition-all duration-300 hover:-translate-y-[1px] hover:bg-[#2d9fe6]">
                        Continue Shopping
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="cart-container mx-auto my-[2%] box-border w-full max-w-[1200px] rounded-[15px] bg-bg p-[20px] shadow-[0_4px_20px_rgba(0,0,0,0.1)]">
            <div className="cart-header mb-[30px] flex items-center justify-between border-b-2 border-slider-color pb-[20px]">
                <h1>Shopping Cart</h1>
                <button onClick={handleClearCart} className="clear-cart-btn rounded-[8px] border-0 bg-[#dc3545] px-[20px] py-[10px] font-[600] text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-[#c82333]">
                    Clear Cart
                </button>
            </div>

            <div className="cart-content grid w-full box-border grid-cols-[2fr_1fr] gap-[30px] max-[1024px]:grid-cols-1">
                <div className="cart-items box-border flex w-full min-w-0 flex-col gap-[20px]">
                    {cartItems.map((item) => {
                        return (
                        <div key={item.id} className={`cart-item box-border relative grid w-full items-center gap-[20px] rounded-[12px] bg-white p-[20px] shadow-[0_2px_10px_rgba(0,0,0,0.1)] transition-all duration-200 hover:-translate-y-[2px] hover:shadow-[0_4px_15px_rgba(0,0,0,0.15)] ${item.productType === 'digital' ? 'digital grid-cols-[100px_2fr_1fr_auto]' : 'grid-cols-[100px_2fr_1fr_1fr_auto]'} max-[767px]:grid-cols-1`}>
                            <div className="cart-item-image h-[100px] w-[100px] overflow-hidden rounded-[8px] max-[767px]:mx-auto">
                                {item.image && item.image.trim() !== '' ? (
                                    <img 
                                        src={getImageUrl(item.image)} 
                                        alt={item.title} 
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div className="no-image flex h-full w-full items-center justify-center bg-[#f8f9fa] text-[0.9rem] text-[#6c757d]" style={{ display: item.image && item.image.trim() !== '' ? 'none' : 'flex' }}>
                                    No Image
                                </div>
                            </div>
                            
                            <div className="cart-item-details flex flex-col gap-[8px] max-[767px]:items-center max-[767px]:text-center">
                                <h3 className="cart-item-title m-0 text-[1.2em] font-[600]">
                                    <Link to={`/store/${item.slug}`} className="text-post-heading no-underline transition-colors duration-300 hover:text-slider-color">{item.title}</Link>
                                </h3>
                                {item.variants && Object.keys(item.variants).length > 0 && (
                                    <div className="cart-item-variants my-[5px]">
                                        {Object.entries(item.variants).map(([key, value]) => (
                                            <span key={key} className="cart-variant mb-[3px] mr-[5px] inline-block rounded-[12px] bg-[#f1f2f6] px-[8px] py-[2px] text-[0.8em] text-[#666]">
                                                {key.charAt(0).toUpperCase() + key.slice(1)}: {value}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <p className="cart-item-price m-0 text-[1.1em] font-[600] text-[#2563eb]">{item.currencySymbol || '₹'}{item.price.toLocaleString()}</p>
                            </div>

                            {item.productType !== 'digital' && (
                            <div className="cart-item-quantity flex justify-center">
                                <div className="quantity-controls flex items-center gap-[8px] rounded-[8px] bg-[#f8f9fa] p-[5px]">
                                    <button 
                                        className="quantity-btn flex h-[30px] w-[30px] items-center justify-center rounded-[5px] border border-[#dee2e6] bg-white font-[600] text-[#333] transition-all duration-300 hover:border-slider-color hover:bg-slider-color hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                        disabled={item.quantity <= 1}
                                    >
                                        -
                                    </button>
                                    <span className="quantity-display min-w-[30px] text-center font-[600] text-[#333]">{item.quantity}</span>
                                    <button 
                                        className="quantity-btn flex h-[30px] w-[30px] items-center justify-center rounded-[5px] border border-[#dee2e6] bg-white font-[600] text-[#333] transition-all duration-300 hover:border-slider-color hover:bg-slider-color hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                        disabled={item.quantity >= 10}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            )}

                            <div className="cart-item-total text-right max-[767px]:text-center">
                                <p className="m-0 text-[1.2em] font-[700] text-post-heading">{item.currencySymbol || '₹'}{(item.price * item.quantity).toLocaleString()}</p>
                            </div>

                            <button 
                                className="remove-item-btn flex h-[35px] w-[35px] items-center justify-center rounded-full border-0 bg-[#dc3545] text-[1.2em] font-[600] text-white transition-all duration-300 hover:scale-110 hover:bg-[#c82333] max-[767px]:mx-auto"
                                onClick={() => handleRemoveItem(item.id)}
                                aria-label="Remove item"
                            >
                                ×
                            </button>
                        </div>
                    );
                    })}
                </div>

                <div className="cart-summary sticky top-[20px] h-fit w-full max-w-full overflow-hidden rounded-[12px] bg-white p-[25px] shadow-[0_2px_10px_rgba(0,0,0,0.1)] max-[1024px]:static">
                    {/* Coupon Code Section */}
                    <div className="coupon-section mb-[30px] border-b-2 border-[#eee] pb-[20px]">
                        <div className="coupon-section-header mb-[15px] flex items-center justify-between">
                            <h2>Coupon Code</h2>
                            <button 
                                className="offers-link-btn flex items-center gap-[6px] border-0 bg-transparent p-0 text-[14px] font-[600] text-post-heading transition-all duration-300 hover:scale-105"
                                onClick={() => setIsOffersDialogOpen(true)}
                                type="button"
                            >
                                <i className="fi fi-br-badge-percent" aria-hidden="true"></i>
                                <span>Offers</span>
                            </button>
                        </div>
                        <div className="coupon-input-group box-border mb-[10px] flex w-full gap-[10px] max-[767px]:flex-col">
                            <input
                                type="text"
                                placeholder="Enter coupon code"
                                value={couponCode}
                                onChange={(e) => {
                                    setCouponCode(e.target.value.toUpperCase());
                                    setCouponError('');
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApplyCoupon();
                                    }
                                }}
                                className="coupon-input box-border min-w-0 flex-1 rounded-[6px] border border-[#d1d5db] px-[12px] py-[10px] text-[14px] transition-colors duration-300 focus:border-slider-color focus:outline-none disabled:cursor-not-allowed disabled:bg-[#f3f4f6]"
                                disabled={couponLoading}
                            />
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleApplyCoupon();
                                }}
                                className="apply-coupon-btn whitespace-nowrap rounded-[6px] border-0 bg-slider-color px-[20px] py-[10px] text-[14px] font-[600] text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-post-heading disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={couponLoading || !couponCode.trim()}
                            >
                                {couponLoading ? 'Applying...' : 'Apply'}
                            </button>
                        </div>
                        {couponError && (
                            <div className="coupon-error mt-[8px] rounded-[4px] bg-[#fef2f2] p-[8px] text-[13px] text-[#dc2626]">{couponError}</div>
                        )}
                        
                        {/* Applied Coupons */}
                        {appliedCoupons.length > 0 && (
                            <div className="applied-coupons">
                                {appliedCoupons.map((coupon, index) => (
                                    <div key={index} className="applied-coupon-item">
                                        <div className="coupon-details">
                                            <div className="coupon-code-name">
                                                <strong>{coupon.code}</strong>
                                                <button
                                                    onClick={() => handleRemoveCoupon(coupon.code)}
                                                    className="remove-coupon-btn"
                                                    aria-label="Remove coupon"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            {(() => {
                                            // Determine discount type (new vs old schema)
                                            const isPercent = coupon.discountType 
                                                ? coupon.discountType === 'PERCENT' 
                                                : coupon.couponType === true;
                                            const discountVal = isPercent
                                                ? (coupon.discountValue || coupon.offerValue || 0)
                                                : (coupon.discountByCountry && coupon.discountByCountry.length > 0 
                                                    ? coupon.discountByCountry[0].amount 
                                                    : (coupon.offerValue || 0));
                                            
                                            if (coupon.appliesToAll) {
                                                const subtotal = calculateSubtotal();
                                                const totalDiscount = isPercent
                                                    ? (subtotal * discountVal) / 100
                                                    : discountVal;
                                                const symbol = (cartItems[0] && cartItems[0].currencySymbol) || '₹';
                                                return (
                                                    <div className="coupon-product-line">
                                                        <span className="coupon-product-name">Applies to All Products</span>
                                                        <span className="coupon-discount">
                                                            {isPercent
                                                                ? `${discountVal}% (${symbol}${Math.round(totalDiscount).toLocaleString()})`
                                                                : `${symbol}${discountVal} (${symbol}${Math.round(totalDiscount).toLocaleString()})`}
                                                        </span>
                                                    </div>
                                                );
                                            } else {
                                                return coupon.applicableProducts.map((product, pIndex) => {
                                                    const productId = product && typeof product === 'object' && product._id 
                                                        ? String(product._id) 
                                                        : String(product);
                                                    
                                                    const cartItem = cartItems.find(item => {
                                                        const itemProductId = String(item.productId || item._id || item.id);
                                                        return itemProductId === productId;
                                                    });
                                                    if (!cartItem) return null;
                                                    
                                                    let productTitle = 'Product';
                                                    if (product && typeof product === 'object' && product.title) {
                                                        productTitle = product.title;
                                                    } else if (coupon.productData && Array.isArray(coupon.productData)) {
                                                        const populatedProduct = coupon.productData.find(p => {
                                                            const pId = p && typeof p === 'object' && p._id ? String(p._id) : String(p);
                                                            return pId === productId;
                                                        });
                                                        if (populatedProduct && typeof populatedProduct === 'object' && populatedProduct.title) {
                                                            productTitle = populatedProduct.title;
                                                        } else if (cartItem.title) {
                                                            productTitle = cartItem.title;
                                                        }
                                                    } else if (cartItem.title) {
                                                        productTitle = cartItem.title;
                                                    }
                                                    
                                                    const itemTotal = cartItem.price * cartItem.quantity;
                                                    const discount = isPercent
                                                        ? (itemTotal * discountVal) / 100
                                                        : discountVal * cartItem.quantity;
                                                    
                                                    const symbol = (cartItems[0] && cartItems[0].currencySymbol) || '₹';
                                                    return (
                                                        <div key={pIndex} className="coupon-product-line">
                                                            <span className="coupon-product-name">{productTitle}</span>
                                                            <span className="coupon-discount">
                                                                {isPercent
                                                                    ? `${discountVal}% (${symbol}${Math.round(discount).toLocaleString()})`
                                                                    : `${symbol}${discountVal} (${symbol}${Math.round(discount).toLocaleString()})`
                                                                }
                                                            </span>
                                                        </div>
                                                    );
                                                });
                                            }
                                        })()}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <h2>Order Summary</h2>
                    <div className="summary-row">
                        <span>Subtotal ({cartItems.reduce((total, item) => total + item.quantity, 0)} items):</span>
                        <span>{(cartItems[0] && cartItems[0].currencySymbol) || '₹'}{calculateSubtotal().toLocaleString()}</span>
                    </div>
                    {appliedCoupons.length > 0 && (
                        <div className="summary-row discount-row">
                            <span>Discount:</span>
                            <span>-{(cartItems[0] && cartItems[0].currencySymbol) || '₹'}{Math.round(calculateDiscount()).toLocaleString()}</span>
                        </div>
                    )}
                    <div className="summary-row">
                        <span>Shipping:</span>
                        <span>Free</span>
                    </div>
                    <div className="summary-row total">
                        <span>Total:</span>
                        <span>{(cartItems[0] && cartItems[0].currencySymbol) || '₹'}{Math.round(calculateTotal()).toLocaleString()}</span>
                    </div>
                    
                    <button 
                        className={`checkout-btn w-full rounded-[10px] border-0 bg-[#47b5ff] px-[16px] py-[12px] text-[1rem] font-[700] text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-[#2d9fe6] disabled:cursor-not-allowed disabled:opacity-70 ${checkoutLoading ? 'loading' : ''}`}
                        onClick={() => {
                            setCheckoutLoading(true);
                            window.location.href = '/checkout';
                        }}
                        disabled={checkoutLoading}
                    >
                        {checkoutLoading ? (
                            <div className="checkout-loading">
                                <div className="spinner"></div>
                                <span>Processing...</span>
                            </div>
                        ) : (
                            'Proceed to Checkout'
                        )}
                    </button>
                    
                    <Link to="/store" className="continue-shopping-link mt-[12px] inline-block text-[0.95rem] font-[600] text-[#2563eb] no-underline hover:underline">
                        Continue Shopping
                    </Link>
                </div>
            </div>
            
            <OffersDialog
                isOpen={isOffersDialogOpen}
                onClose={() => setIsOffersDialogOpen(false)}
                cartItems={cartItems}
                appliedCoupons={appliedCoupons}
                onApplyCoupon={handleApplyCoupon}
            />
        </div>
    );
}

